import React, { useEffect, useState, useRef, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Menu, Dialog, Transition, Popover, Switch } from "@headlessui/react";
import clsx from "clsx";
import Layout from "../../layouts";
import Dropzone from "../../components/Dropzone";
import FieldError from "../../components/FieldError";
import Breadcrumb from "../../components/Breadcrumb";
import { useOrganisationStore } from "../../store";
import { validateOrganisationReplForm } from "../../services/validators";

export default function ReplForm() {
  const formRef = useRef();
  const navigate = useNavigate();
  const [messages, setMessages] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const store = useOrganisationStore();

  useEffect(() => {
    if (!store.form.agree) {
      navigate("/organisation");
      return;
    }
  }, [navigate, store.form.agree]);

  useEffect(() => {
    window.addEventListener("beforeunload", alertBeforeLeaving);
    window.scrollTo(0, 0);
    return () => {
      window.removeEventListener("beforeunload", alertBeforeLeaving);
    };
  }, []);

  const alertBeforeLeaving = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };

  const readFileASync = (file) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  };

  const onValidate = () => {
    const validated = validateOrganisationReplForm(store.form);

    const errors = validated.reduce(
      (accumulator, value) => ({
        ...accumulator,
        [value]: "Must not be empty",
      }),
      {}
    );
    setMessages(errors);

    if (Object.keys(errors).length > 0) {
      setShowAlert(true);
      formRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      setShowAlert(false);
      navigate(`/organisation/app/repl/confirmation`);
    }
  };

  return (
    <Layout mType="organisation">
      <div className="bg-gray-100">
        <div className="container mx-auto">
          <div className="lg:py-5 lg:px-0 px-0 py-4">
            <ul className="form-steps">
              <li className="active">Declaration</li>
              <li className="active">Form</li>
              <li>Confirmation</li>
              <li>Completion</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container mx-auto" ref={formRef}>
        <div className="lg:py-8 lg:px-0 px-4 py-4">
          <Breadcrumb mType="organisation" appType="repl" />

          <h3 className="font-bold text-lg mb-8">Card Replacement</h3>
          {showAlert && (
            <div
              class="flex p-4 mb-8 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
              role="alert"
            >
              <svg
                aria-hidden="true"
                class="flex-shrink-0 inline w-5 h-5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="sr-only">Info</span>
              <div>
                <span class="font-medium">Error:</span> Please fix the errors
                below before continuing to the next step.
              </div>
            </div>
          )}
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Card Type</h3>
            </div>
            <div className="px-8 py-8" id="fg-cardType">
              <div className="flex items-center">
                <div className="lg:w-72 w-1/2">
                  <label>* Card Type:</label>
                </div>
                <div className="flex-1 ml-4 text-blue-700">
                  {store.form.cardType === "reader" && "Reader Card"}
                  {store.form.cardType === "borrower" && "Borrower Card"}
                </div>
              </div>
            </div>
          </div>
          <div className="border large-shadow mb-8">
            <div className="border-b py-4 px-8 flex items-center justify-between">
              <h3 className="font-bold">Card Pickup Location</h3>
            </div>
            <div className="px-8 py-8" id="fg-cardType">
              <div class="flex items-center mb-4">
                <input
                  type="radio"
                  id="pickup-mmw"
                  name="pickup_location"
                  value="mmw"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("pickupLocation", "mmw")}
                  checked={store.form.pickupLocation === "mmw"}
                />
                <label for="pickup-mmw" class="ml-2">
                  MMW Library
                </label>
              </div>
              <div class="flex items-center">
                <input
                  type="radio"
                  id="pickup-tkosc"
                  name="pickup_location"
                  value="tkosc"
                  class="w-4 h-4"
                  onChange={() => store.updateForm("pickupLocation", "tkosc")}
                  checked={store.form.pickupLocation === "tkosc"}
                />
                <label for="pickup-tkosc" class="ml-2">
                  TKOSC Library
                </label>
              </div>

              <FieldError field={messages.pickupLocation} mt={5} />
            </div>
          </div>
          <div className="flex items-center justify-center my-8">
            <button
              className="border rounded-full px-4 py-2 w-48 text-center inline-block text-gray-600 hover:bg-gray-200 transition duration-200 focus:outline-none"
              onClick={() => navigate(`/organisation/app/repl`)}
            >
              Back
            </button>
            <button
              className="border border-orange-600 rounded-full px-4 py-2 w-48 text-center inline-block bg-orange-600 text-white ml-8 hover:bg-orange-700 transition duration-200 focus:outline-none"
              onClick={onValidate}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
